import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../styles/policy.css';

function TabNav({ type }) {
  return (
    <nav className="policyPage__tabs" aria-label="Policy navigation">
      <Link
        className={`policyPage__tab${type === 'privacy' ? ' policyPage__tab--active' : ''}`}
        to="/policy/privacy"
      >
        Privacy Policy
      </Link>
      <Link
        className={`policyPage__tab${type === 'store' ? ' policyPage__tab--active' : ''}`}
        to="/policy/store"
      >
        Store Policy
      </Link>
    </nav>
  );
}

function PrivacyPolicy() {
  return (
    <div className="policyPage__layout">
      {/* Sticky sidebar TOC */}
      <aside className="policyPage__sidebar" aria-label="Table of contents">
        <div className="policyPage__toc">
          <p className="policyPage__tocLabel">On this page</p>
          <ul className="policyPage__tocList">
            <li><a className="policyPage__tocLink" href="#priv-intro">Overview</a></li>
            <li><a className="policyPage__tocLink" href="#priv-collect">Information We Collect</a></li>
            <li><a className="policyPage__tocLink" href="#priv-use">How We Use It</a></li>
            <li><a className="policyPage__tocLink" href="#priv-share">Sharing of Information</a></li>
            <li><a className="policyPage__tocLink" href="#priv-security">Data Security</a></li>
            <li><a className="policyPage__tocLink" href="#priv-rights">Your Rights</a></li>
            <li><a className="policyPage__tocLink" href="#priv-links">Third-Party Links</a></li>
            <li><a className="policyPage__tocLink" href="#priv-changes">Changes</a></li>
            <li><a className="policyPage__tocLink" href="#priv-contact">Contact Us</a></li>
          </ul>
        </div>
      </aside>

      {/* Main content */}
      <div className="policyPage__body">
        <div className="policyPage__section" id="priv-intro">
          <p className="policyPage__text">
            "Elena RD art jewelry &amp; accessories" ("we," "our," "us") is committed to protecting
            your privacy. This Privacy Policy explains how we collect, use, and safeguard your
            personal information when you visit www.elenard.com (the "Website").
          </p>
        </div>

        <hr className="policyPage__divider" />

        <div className="policyPage__section" id="priv-collect">
          <h2 className="policyPage__sectionTitle">1. Information We Collect</h2>
          <ul className="policyPage__list">
            <li>
              <strong>Personal Information:</strong> Name, email, shipping address, phone number,
              and payment details when you place an order or register.
            </li>
            <li>
              <strong>Payment Information:</strong> Payments are processed securely through
              third-party providers; we do not store your card details.
            </li>
            <li>
              <strong>Non-Personal Information:</strong> IP address, browser type, cookies, and
              usage data for improving services.
            </li>
            <li>
              <strong>Communication Data:</strong> Emails, messages, and customer support
              interactions are stored for quality assurance.
            </li>
          </ul>
        </div>

        <div className="policyPage__section" id="priv-use">
          <h2 className="policyPage__sectionTitle">2. How We Use Your Information</h2>
          <ul className="policyPage__list">
            <li>To process and fulfill your orders.</li>
            <li>To provide customer service and respond to inquiries.</li>
            <li>To send promotional emails and updates (you can opt out anytime).</li>
            <li>To enhance website functionality and prevent fraud.</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </div>

        <div className="policyPage__section" id="priv-share">
          <h2 className="policyPage__sectionTitle">3. Sharing of Information</h2>
          <ul className="policyPage__list">
            <li>
              <strong>Service Providers:</strong> We share necessary data with shipping companies,
              payment processors, and email marketing services.
            </li>
            <li>
              <strong>Legal Compliance:</strong> We may disclose information when required by law.
            </li>
          </ul>
        </div>

        <div className="policyPage__section" id="priv-security">
          <h2 className="policyPage__sectionTitle">4. Data Security</h2>
          <p className="policyPage__text">
            We implement encryption, secure servers, and strict protocols to safeguard your
            information. However, users must protect their own account credentials.
          </p>
        </div>

        <div className="policyPage__section" id="priv-rights">
          <h2 className="policyPage__sectionTitle">5. Your Rights</h2>
          <ul className="policyPage__list">
            <li>Request access, correction, or deletion of your data.</li>
            <li>Opt out of marketing emails.</li>
            <li>Disable cookies via browser settings.</li>
          </ul>
        </div>

        <div className="policyPage__section" id="priv-links">
          <h2 className="policyPage__sectionTitle">6. Third-Party Links</h2>
          <p className="policyPage__text">
            Our Website may contain links to external sites. We are not responsible for their
            privacy policies.
          </p>
        </div>

        <div className="policyPage__section" id="priv-changes">
          <h2 className="policyPage__sectionTitle">7. Changes to This Policy</h2>
          <p className="policyPage__text">
            We may update this Privacy Policy periodically. Continued use of our Website indicates
            acceptance of changes.
          </p>
        </div>

        <div className="policyPage__section" id="priv-contact">
          <h2 className="policyPage__sectionTitle">Contact Us</h2>
          <p className="policyPage__text">
            For any privacy-related questions, please reach us at{' '}
            <a className="policyPage__contactLink" href="mailto:info@elenard.com">
              info@elenard.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function StorePolicy() {
  return (
    <div className="policyPage__layout">
      {/* Sticky sidebar TOC */}
      <aside className="policyPage__sidebar" aria-label="Table of contents">
        <div className="policyPage__toc">
          <p className="policyPage__tocLabel">On this page</p>
          <ul className="policyPage__tocList">
            <li><a className="policyPage__tocLink" href="#store-overview">Overview</a></li>
            <li><a className="policyPage__tocLink" href="#store-shipping">Return Shipping</a></li>
            <li><a className="policyPage__tocLink" href="#store-initiate">How to Return</a></li>
            <li><a className="policyPage__tocLink" href="#store-refund">Refund Processing</a></li>
          </ul>
        </div>
      </aside>

      {/* Main content */}
      <div className="policyPage__body">
        <div className="policyPage__section" id="store-overview">
          <p className="policyPage__text">
            Returns are accepted within <strong>14 days</strong> from the day the parcel is
            delivered. Please read the full policy below before initiating a return.
          </p>
        </div>

        <div className="policyPage__highlight">
          <p className="policyPage__highlightTitle">Key Points at a Glance</p>
          <div className="policyPage__highlightItem">
            <span className="policyPage__highlightDot" />
            <span>14-day return window from delivery date.</span>
          </div>
          <div className="policyPage__highlightItem">
            <span className="policyPage__highlightDot" />
            <span>Customer covers return shipping costs and must provide a tracking number.</span>
          </div>
          <div className="policyPage__highlightItem">
            <span className="policyPage__highlightDot" />
            <span>
              Contact us before sending — parcels returned without prior communication will not be
              accepted.
            </span>
          </div>
          <div className="policyPage__highlightItem">
            <span className="policyPage__highlightDot" />
            <span>Full refund issued after inspection confirms item is in original condition.</span>
          </div>
        </div>

        <hr className="policyPage__divider" />

        <div className="policyPage__section" id="store-shipping">
          <h2 className="policyPage__sectionTitle">Return Shipping</h2>
          <p className="policyPage__text">
            The buyer is responsible for return shipping costs with a tracking number, and for any
            loss in value if the item is not returned in its original condition.
          </p>
        </div>

        <div className="policyPage__section" id="store-initiate">
          <h2 className="policyPage__sectionTitle">How to Initiate a Return</h2>
          <p className="policyPage__text">
            If you would like to return your order, please email us through the{' '}
            <Link className="policyPage__contactLink" to="/contact">
              Contact Us
            </Link>{' '}
            section on our website. Parcels shipped back without prior communication will not be
            accepted for a refund.
          </p>
        </div>

        <div className="policyPage__section" id="store-refund">
          <h2 className="policyPage__sectionTitle">Refund Processing</h2>
          <p className="policyPage__text">
            A full refund will be processed once the purchased hair jewelry is returned to "Elena RD
            art jewelry &amp; accessories" and inspected to ensure it is in its original condition.
            Refunds are contingent on item quality assessment.
          </p>
          <p className="policyPage__text">
            "Elena RD art jewelry &amp; accessories" hopes this policy will never need to be used,
            but it is provided for clarity.
          </p>
        </div>
      </div>
    </div>
  );
}

export function PolicyPage() {
  const { type } = useParams();

  const title = useMemo(() => {
    if (type === 'privacy') return 'Privacy Policy';
    if (type === 'store') return 'Store Policy';
    return 'Policies';
  }, [type]);

  const subtitle = useMemo(() => {
    if (type === 'privacy') return 'Effective Date: March 2025';
    if (type === 'store') return 'Returns & Refunds';
    return null;
  }, [type]);

  return (
    <main className="policyPage">
      {/* Dark hero */}
      <div className="policyPage__hero">
        <div className="container">
          <div className="policyPage__heroInner">
            <div className="policyPage__badge">✦ Legal</div>
            <h1 className="policyPage__title">{title}</h1>
            {subtitle && <p className="policyPage__meta">{subtitle}</p>}
            <TabNav type={type} />
          </div>
        </div>
      </div>

      {/* Dark content */}
      {(type === 'privacy' || type === 'store') && (
        <div className="policyPage__content">
          <div className="container">
            {type === 'privacy' ? <PrivacyPolicy /> : <StorePolicy />}
          </div>
        </div>
      )}
    </main>
  );
}
